/* eslint-disable react/function-component-definition */
/* eslint-disable import/extensions */
/* eslint-disable import/no-named-as-default */
import React, { useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  breakpoints,
  fontSize,
  Modal,
  palette,
  spacing,
  Button,
  Typography,
} from '@playdapp/ui';

import useOpenControl from 'hooks/useOpenControl';
import useSimplex from 'hooks/useSimplex';
import useMoonpay from 'hooks/useMoonpay';
import useTransak from 'hooks/useTransak';
import useWeb3Info from 'hooks/useWeb3Info';
import { chainIdToMainNetworkName } from 'lib/network';
import { setComma } from 'lib/util';
import { swapLinks } from 'data/crypto-token';

import Icon, { Icons } from '@/components/Icon';
import Redirect from '../Redirect';

type Props = {
  isOpen: boolean;
  handleOpen: (isOpen: boolean) => void;
  balance: number;
  currency: string;
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const BalanceBlock = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing.xl};
  font-size: ${fontSize.h4};
  font-weight: 900;

  ${breakpoints.down('md')} {
    font-size: ${fontSize.p1};
  }
`;

const BalanceText = styled.span`
  margin-left: 0.5rem;
  color: ${palette.primary600};
`;

const AddFundsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxl};
  padding: ${spacing.l};
  margin-bottom: ${spacing.xl};
  border-radius: 1rem;
  background-color: ${palette.gray200};

  button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    background-color: ${palette.white};
    font-weight: 600;
  }
`;

const AddFundsTitle = styled.p`
  font-weight: 700;
  text-align: center;
`;

const SymbolBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const DescriptionText = styled.p`
  text-align: center;

  ${breakpoints.down('lg')} {
    font-size: ${fontSize.p4};
  }
`;

const CardModalBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CardModalTitle = styled.div`
  text-align: center;
  line-height: 1.5;
`;

const CardModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${spacing.l};
  margin-top: ${spacing.xl};
  background-color: ${palette.gray200};
  border-radius: ${spacing.m};

  & > span {
    margin-bottom: ${spacing.xl};
  }

  div {
    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.8;
    }
  }

  div + div {
    margin-top: ${spacing.m};
  }
`;

const CardModalButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.s} ${spacing.xxl};
  width: 100%;
  height: ${({ theme }) => theme.rem(48)};
  border: 1px solid ${palette.black};
  border-radius: ${spacing.xl};
  background-color: ${palette.white};
  cursor: pointer;

  p {
    margin-right: ${spacing.xs};
  }
`;

const SymbolList = {
  polygon: {
    card: ['matic', 'weth'],
    swap: ['matic', 'weth'],
    desc: 'You can buy Matic, WETH and PLA with your credit card.',
  },
  ethereum: {
    card: ['eth'],
    swap: ['eth', 'weth'],
    desc: 'You can buy PLA and ETH with your credit card.',
  },
  solana: {
    card: [],
    swap: [],
    desc: '',
  },
  klaytn: {
    card: [],
    swap: [],
    desc: '',
  },
};

const BalanceModal = ({ isOpen, currency, balance, handleOpen }: Props) => {
  const { chainId } = useWeb3Info();
  const [isCardModalOpen, handleCardModalOpen] = useOpenControl();
  const {
    SimplexModal,
    isOpen: isOpenSimplex,
    handleOpen: handleOpenSimplex,
  } = useSimplex();

  const {
    MoonPayModal,
    isOpen: isOpenMoonpay,
    handleOpen: handleOpenMoonpay,
  } = useMoonpay();

  const { handleOpenTransak } = useTransak();

  const networkName = useMemo(
    () => (chainId ? chainIdToMainNetworkName[chainId] : 'polygon'),
    [chainId],
  );

  useEffect(() => {
    handleOpen(false);
    handleCardModalOpen(false);
  }, [isOpenSimplex]);

  return (
    <>
      <Modal
        width={{ lg: '500px', xl: '500px' }}
        shouldCloseOnOverlayClick
        isOpen={isOpen}
        handleOpen={handleOpen}
      >
        <ModalContent>
          <BalanceBlock>
            My Balance :{' '}
            <BalanceText>
              {balance
                ? `${setComma(balance)} ${currency?.toUpperCase()}`
                : '-'}
            </BalanceText>
          </BalanceBlock>
          <AddFundsBlock>
            <AddFundsTitle>Add Funds</AddFundsTitle>
            <Button
              onClick={() => {
                handleCardModalOpen(true);
              }}
              variant="outline"
              color="secondary"
              size="lg"
            >
              Buy with card
              <SymbolBox>
                {SymbolList[networkName]?.card.map((symbol) => (
                  <Icon
                    key={`card-${symbol}`}
                    name={`currency-${symbol}` as Icons}
                    size={24}
                    alt={symbol}
                  />
                ))}
              </SymbolBox>
            </Button>
            <Redirect to={swapLinks[networkName]}>
              <Button variant="outline" color="secondary" size="lg">
                Swap
                <SymbolBox>
                  {SymbolList[networkName]?.swap.map((symbol) => (
                    <Icon
                      key={`swap-${symbol}`}
                      name={`currency-${symbol}` as Icons}
                      size={24}
                      alt={symbol}
                    />
                  ))}
                </SymbolBox>
              </Button>
            </Redirect>
          </AddFundsBlock>
          <DescriptionBlock>
            {networkName === 'ethereum' ? (
              <DescriptionText>
                You can buy{' '}
                <b>{SymbolList[networkName]?.card[0]?.toUpperCase()}</b> and{' '}
                <b>{SymbolList[networkName]?.card[1]?.toUpperCase()}</b> with
                your credit card.
              </DescriptionText>
            ) : (
              <DescriptionText>
                You can buy{' '}
                <b>{SymbolList[networkName]?.card[1]?.toUpperCase()}</b>,{' '}
                <b>{SymbolList[networkName]?.card[2]?.toUpperCase()}</b> and{' '}
                <b>{SymbolList[networkName]?.card[0]?.toUpperCase()}</b> with
                your credit card.
              </DescriptionText>
            )}
          </DescriptionBlock>
        </ModalContent>
      </Modal>

      <Modal
        width={{ lg: '500px', xl: '500px' }}
        isOpen={isCardModalOpen}
        handleOpen={handleCardModalOpen}
      >
        <CardModalBlock>
          <CardModalTitle>
            <Typography type="h4">Buy with card</Typography>
          </CardModalTitle>
          <CardModalContent>
            <Typography type="b1">Add Funds</Typography>
            <CardModalButton onClick={() => handleOpenSimplex(!isOpenSimplex)}>
              <Typography type="b3">To Simplex</Typography>
              <Icon
                name="simplexLogo"
                width={81}
                height={39}
                alt="card-brand"
              />
            </CardModalButton>
            <CardModalButton onClick={() => handleOpenMoonpay(!isOpenMoonpay)}>
              <Typography type="b3">To Moonpay</Typography>
              <Icon
                name="moonpayLogo"
                width={81}
                height={39}
                alt="card-brand"
              />
              <MoonPayModal />
            </CardModalButton>
            <CardModalButton onClick={handleOpenTransak}>
              <Typography type="b3">To Transak</Typography>
              <Icon
                name="transakLogo"
                width={81}
                height={39}
                alt="card-brand"
              />
            </CardModalButton>
          </CardModalContent>
        </CardModalBlock>
      </Modal>

      <SimplexModal />
    </>
  );
};

export default React.memo(BalanceModal);
